const MAX_SOURCE_BYTES = 25 * 1024 * 1024

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () =>
      reject(new Error('That image couldn’t be read. Try exporting it as a JPG or PNG.'))
    image.src = src
  })
}

/**
 * Shrinks a poster in the browser before upload so a large phone photo
 * uploads quickly and loads fast for visitors.
 */
export async function preparePoster(file: File, maxWidth = 1400): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('That file isn’t an image. Choose a JPG, PNG or WebP poster.')
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error('That image is over 25MB. Export a smaller version and try again.')
  }

  const objectUrl = URL.createObjectURL(file)
  try {
    const image = await loadImage(objectUrl)
    const scale = Math.min(1, maxWidth / image.naturalWidth)
    const width = Math.round(image.naturalWidth * scale)
    const height = Math.round(image.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Your browser couldn’t process that image.')

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
    context.drawImage(image, 0, 0, width, height)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error('Your browser couldn’t process that image.')),
        'image/jpeg',
        0.86,
      )
    })
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}
